import { useEffect, useState } from 'react';
import Modal from '../Modal';
import { repositorioFabricanteMaquina } from '../../sync/repositories/fazendas';
import { extrairMensagensErro } from '../../utils/errorUtils';

/**
 * Modal de cadastro rápido de Fabricante de Máquina.
 *
 * Props:
 *   isOpen, onClose
 *   onSuccess(novoFabricante)
 *   tiposMaquina     — lista [{id, nome, ...}] carregada pela página pai
 *   tipoSelecionadoId — opcional; pré-seleciona esse tipo no checklist
 */
export default function ModalCadastroFabricanteMaquina({
  isOpen,
  onClose,
  onSuccess,
  tiposMaquina = [],
  tipoSelecionadoId = null,
}) {
  const [nome, setNome] = useState('');
  const [tiposSelecionados, setTiposSelecionados] = useState(new Set());
  const [salvando, setSalvando] = useState(false);
  const [erros, setErros] = useState([]);

  useEffect(() => {
    if (!isOpen) {
      setNome('');
      setErros([]);
      setSalvando(false);
      return;
    }
    // Pré-seleciona o tipo atualmente escolhido no formulário principal
    // (UX: o usuário abriu o modal porque aquele tipo já estava escolhido).
    if (tipoSelecionadoId) {
      setTiposSelecionados(new Set([Number(tipoSelecionadoId)]));
    } else {
      setTiposSelecionados(new Set());
    }
  }, [isOpen, tipoSelecionadoId]);

  const toggleTipo = (id) => {
    setTiposSelecionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSalvar = async (e) => {
    e?.preventDefault?.();
    if (!nome.trim()) {
      setErros(['O nome é obrigatório.']);
      return;
    }
    setSalvando(true);
    setErros([]);
    try {
      const resp = await repositorioFabricanteMaquina.criar({
        nome: nome.trim(),
        ativo: true,
        tipos_maquina_ids: Array.from(tiposSelecionados),
      });
      onSuccess?.(resp.data);
      onClose?.();
    } catch (error) {
      setErros(extrairMensagensErro(error));
    } finally {
      setSalvando(false);
    }
  };

  const tiposOrdenados = [...tiposMaquina].sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));

  return (
    <Modal isOpen={isOpen} onClose={salvando ? () => {} : onClose} title="Cadastrar Fabricante" size="md">
      <form onSubmit={handleSalvar} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
          <input
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300"
            placeholder="Ex: John Deere, Massey Ferguson..."
            autoFocus
            disabled={salvando}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tipos de Máquina</label>
          {tiposOrdenados.length === 0 ? (
            <p className="text-sm text-gray-500">Nenhum tipo de máquina cadastrado.</p>
          ) : (
            <div className="max-h-48 overflow-y-auto border border-gray-200 rounded p-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
              {tiposOrdenados.map((t) => (
                <label key={t.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50 px-1 py-0.5 rounded">
                  <input
                    type="checkbox"
                    checked={tiposSelecionados.has(t.id)}
                    onChange={() => toggleTipo(t.id)}
                    disabled={salvando}
                    className="rounded text-green-600 focus:ring-green-500"
                  />
                  <span>{t.nome}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {erros.length > 0 && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 space-y-1">
            {erros.map((m, i) => <div key={i}>{m}</div>)}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={salvando}
            className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={salvando}
            className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:bg-blue-300"
          >
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
